// React Core

import type {
  AirDatepickerLocale,
  AirDatepickerOptions,
  AirDatepickerPosition,
} from 'air-datepicker';
import AirDatepicker from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
// Hooks
import { useTranslation } from 'react-i18next';
// External Libraries
import { type ColumnDefinition, ReactTabulator, type ReactTabulatorOptions } from 'react-tabulator';
import type { CellComponent, Tabulator } from 'tabulator-tables';
// Assets
import IconPlay from '@/assets/icon/icon-play-tabulator.svg';
import useSuddenEvent from '@/domains/statistic/hooks/useSuddenEvent';
// Types
import type {
  ChartSeries,
  CntQuery,
  DateTimeStateCnt,
  DateTimeStateFull,
  EventTypeOption,
  SuddenEvent,
  SuddenEventListResponse,
  TimeQuery,
} from '@/domains/statistic/types/sudden-event.types';
import Button from '@/shared/components/ui/Button/Button';
// Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import Select from '@/shared/components/ui/Select/Select';
import { URLS } from '@/shared/config/urls';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
// Utilities & Configs
import { formatDateTime, formatFullDateTime } from '@/shared/utils/date';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';

// --- Tabulator Configuration ---
const commonColumnSettings: Partial<ColumnDefinition> = {
  hozAlign: 'center',
  headerHozAlign: 'center',
  resizable: false,
};

const suddenEventTabulator: ColumnDefinition[] = [
  {
    title: 'No',
    formatter: (cell: CellComponent<SuddenEvent>) => {
      const row = cell.getRow();
      const page = (row.getTable().getPage() as number) || 1;
      const pageSize = row.getTable().getPageSize() as number;
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    headerSort: false,
    ...commonColumnSettings,
  },
  {
    title: '발생일시',
    field: 'timestamp',
    widthGrow: 2,
    headerSort: false,
    ...commonColumnSettings,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 2,
    headerSort: false,
    ...commonColumnSettings,
  },
  {
    title: '접근로',
    field: 'road_name',
    widthGrow: 2,
    headerSort: false,
    ...commonColumnSettings,
  },
  {
    title: '이벤트 유형',
    field: 'type',
    widthGrow: 1,
    headerSort: false,
    ...commonColumnSettings,
  },
  {
    title: '차종',
    field: 'vehicle_type',
    widthGrow: 1,
    headerSort: false,
    ...commonColumnSettings,
  },
  // DO NOT DELETE - for future use, may be added back later
  // {
  //   title: '차선방향',
  //   field: 'lane_moving_direction',
  //   widthGrow: 1,
  //   headerSort: false,
  //   ...commonColumnSettings,
  // },
  {
    title: '차로',
    field: 'lane_number',
    widthGrow: 1,
    headerSort: false,
    ...commonColumnSettings,
  },
  // DO NOT DELETE - for future use, may be added back later
  // {
  //   title: '진행방향',
  //   field: 'lane_direction',
  //   widthGrow: 1,
  //   headerSort: false,
  //   ...commonColumnSettings,
  // },
  {
    title: '이미지',
    field: 'image',
    widthGrow: 1,
    headerSort: false,
    ...commonColumnSettings,
    formatter: () => {
      return `<img src="${IconPlay}" alt="Play Icon" style="margin: auto; cursor: pointer;" />`;
    },
  },
];

// --- Chart Configuration (base, without theme-dependent values) ---
// Note: `colors` is set in chartOptionsWithProps (theme-aware via colorStore),
// not here, so this base stays free of color literals.
const optionsChartBase: Omit<ApexCharts.ApexOptions, 'chart'> = {
  markers: {
    size: 8,
    hover: {
      size: 8,
    },
  },
  stroke: {
    show: true,
    curve: 'straight',
    width: 1.5,
  },
  xaxis: {
    categories: [],
  },
  yaxis: {
    min: 0,
    max: 100,
  },
};

// --- Component ---
const SuddenEventManagementPage: React.FC = () => {
  const { preferenceData } = usePreferenceContext();
  const { t, i18n } = useTranslation();
  const searchRef = useRef<HTMLInputElement>(null);
  const tbRef = useRef<Tabulator | null>(null);
  // const [page, setPage] = useState<number>(1); // current Page Number현재 페이지 번호
  const rowsPerPage: number = 10; // 한 페이지에 표시할 데이터 개수
  const [type, setType] = useState<string>('All');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>(''); //함체, 네트워크

  // const excludeEventType: string[] = [
  //   'EVT_TP_TMP', //함체 온도
  //   'EVT_TP_HUM', //함체 습도
  //   'EVT_TP_NW', //네트워크
  //   'EVT_TP_PW', //함체 전력
  //   'EVT_TP_DO', //문 열림
  // ];

  const eventTypes: string[] = useMemo(() => {
    try {
      const prefItem = preferenceData?.find((item) => item.preference_key === 'event_types');
      return JSON.parse(prefItem?.preference_value || '[]');
    } catch {
      return [];
    }
  }, [preferenceData]);

  const { commonListData } = useCommonCodes({
    optionParams: 'upper-code=EVT_TP',
  });

  const filteredEventTypeOptions: EventTypeOption[] = useMemo(() => {
    const commonCodes = commonListData?.EVT_TP;
    const list = (commonCodes?.code || []).map((code, index: number) => ({
      label: commonCodes?.name[index] || '',
      value: code ?? '',
    }));
    return [
      { label: '전체', value: 'All' }, // 전체 옵션 추가
      ...list.filter((option) => eventTypes.includes(option.value)),
    ];
  }, [commonListData, eventTypes]);


  // Chart data state
  const [averageWaitTimeValues, setAverageWaitTimeValues] = useState<ChartSeries[]>(() =>
    filteredEventTypeOptions.map((opt) => ({
      name: opt.label,
      data: new Array<number>(12).fill(0),
    }))
  );
  //graph
  const [categories, setCategories] = useState<string[]>([]); //MM-DD hh:mm - graph x display
  // const [fullCategories, setFullCategories] = useState<string[]>([]); //YYYY-MM-DD hh:mm - compare
  const [yaxisMax, setYaxisMax] = useState<number>(100);

  const today = new Date();
  const firstDateInit = new Date(today);
  firstDateInit.setHours(0, 0, 0, 0); // 자정으로 설정
  const secondDateInit = new Date(today);

  // Utility function to calculate interval (difference in minutes, divided by 12)
  const calculateInterval = useCallback((startDate: Date, endDate: Date): number => {
    const timeDifference = endDate.getTime() - startDate.getTime(); // Milliseconds
    const interval = timeDifference / 12 / 60000; // Convert to minutes
    return Math.max(1, Math.floor(interval)); // interval must be at least 1
  }, []);

  const [dateTime1, setDateTime1] = useState<DateTimeStateFull>({
    start_date: formatFullDateTime(firstDateInit),
    end_date: formatFullDateTime(secondDateInit),
  });
  const [dateTime2, setDateTime2] = useState<DateTimeStateCnt>({
    start_date: formatDateTime(firstDateInit),
    end_date: formatDateTime(secondDateInit),
    interval: calculateInterval(firstDateInit, secondDateInit), // Initial interval calculation
  });

  const [queryParamsTime, setQueryParamsTime] = useState<TimeQuery>(''); // queryParams 상태
  const [queryParamsCnt, setQueryParamsCnt] = useState<CntQuery>(''); // queryParams 상태
  interface UseSuddenEventReturn {
    trafficEventTime: SuddenEventListResponse | undefined;
    trafficEventCnt:
      | {
          data: {
            aggregate_start_time: string;
            aggregate_end_time: string;
            type: string;
            cnt: number;
          }[];
        }
      | undefined;
    refetchTrafficEventCnt: () => void;
    isLoading: boolean;
  }
  const { trafficEventTime, trafficEventCnt } = useSuddenEvent({
    queryParamsTime: queryParamsTime,
    queryParamsCnt: queryParamsCnt,
  }) as UseSuddenEventReturn;

  const data: SuddenEventListResponse['data'] | undefined = trafficEventTime?.data;


  //이벤트 유형 선택 - select sudden evenet type
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val: string = e.target.value;
    setSelectedOption(val);
    setType(val);
  };

  const options: EventTypeOption[] = useMemo(
    () => [...filteredEventTypeOptions],
    [filteredEventTypeOptions]
  );

  const [activeButton, setActiveButton] = useState<string>('');

  const handleSearch = useCallback(
    (
      inputVal: string = searchInput,
      currentType: string = type,
      fullStartDate: string = dateTime1.start_date,
      fullEndDate: string = dateTime1.end_date,
      cntStartDate: string = dateTime2.start_date,
      cntEndDate: string = dateTime2.end_date,
      interval: number = dateTime2.interval
    ) => {
      let timeQuery = `start_time=${fullStartDate}&end_time=${fullEndDate}`;
      if (inputVal !== '') {
        timeQuery += `&input=${inputVal}`;
        // setSearchInput(inputVal);
      } else {
        // setSearchInput("");
      }

      let typesQuery = '';
      if (currentType === 'All') {
        const types = filteredEventTypeOptions
          .filter((opt) => opt.value !== 'All')
          .map((option) => `&type=${option.value}`)
          .join('');
        typesQuery = types;
      } else if (currentType !== '') {
        typesQuery = `&type=${currentType}`;
      }
      timeQuery += typesQuery;

      let cntQuery = `start_time=${cntStartDate}&end_time=${cntEndDate}&interval=${interval}`;

      if (inputVal !== '') {
        cntQuery += `&input=${inputVal}`;
      }
      cntQuery += typesQuery;

      setQueryParamsTime(timeQuery as TimeQuery);
      setQueryParamsCnt(cntQuery as CntQuery);

      const startDate = new Date(`${cntStartDate}:00`);
      const endDate = new Date(`${cntEndDate}:00`);
      const timeDifference = endDate.getTime() - startDate.getTime(); // 밀리초 단위 차이
      const timeInterval = timeDifference / 12; // 12개의 구간으로 나누기
      const newCategories: string[] = new Array(12);
      const newFullCategories: string[] = new Array(12);

      for (let i = 0; i < 12; i++) {
        const intervalEnd = new Date(startDate.getTime() + timeInterval * (i + 1));
        const categoryLabel = `${String(intervalEnd.getMonth() + 1).padStart(2, '0')}-${String(intervalEnd.getDate()).padStart(2, '0')} ${String(intervalEnd.getHours()).padStart(2, '0')}:${String(intervalEnd.getMinutes()).padStart(2, '0')}`;
        const fullCategoryLabel = `${intervalEnd.getFullYear()}-${String(intervalEnd.getMonth() + 1).padStart(2, '0')}-${String(intervalEnd.getDate()).padStart(2, '0')} ${String(intervalEnd.getHours()).padStart(2, '0')}:${String(intervalEnd.getMinutes()).padStart(2, '0')}`;
        newCategories[i] = categoryLabel;
        newFullCategories[i] = fullCategoryLabel;
      }
      setCategories(newCategories);
      // setFullCategories(newFullCategories);
    },
    [
      filteredEventTypeOptions,
      searchInput,
      type,
      dateTime1.start_date,
      dateTime1.end_date,
      dateTime2.start_date,
      dateTime2.end_date,
      dateTime2.interval,
    ]
  );

  const setTimeAndQuery = useCallback(
    (firstDate: Date, secondDate: Date, interval: number): void => {
      setDateTime1({
        start_date: formatFullDateTime(firstDate),
        end_date: formatFullDateTime(secondDate),
      });
      setDateTime2({
        start_date: formatDateTime(firstDate),
        end_date: formatDateTime(secondDate),
        interval: interval,
      });
      handleSearch(
        searchInput,
        type,
        formatFullDateTime(firstDate),
        formatFullDateTime(secondDate),
        formatDateTime(firstDate),
        formatDateTime(secondDate),
        interval
      );
    },
    [searchInput, type, handleSearch]
  );

  const handleButtonClick = (label: string) => {
    setActiveButton(label);
    if (label === '1시간') {
      setOneHour();
    } else if (label === '1일') {
      setOneDay();
    } else if (label === '1개월') {
      setOneMonth();
    } else if (label === '') {
      setCustomTime();
    }
  };

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  const calendarMake = useCallback(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const [datePart1, timePart1] = dateTime2.start_date.split(' ');
    const firstDate = new Date(`${datePart1}T${timePart1}:00`);
    const [datePart2, timePart2] = dateTime2.end_date.split(' ');
    const secondDate = new Date(`${datePart2}T${timePart2}:00`);

    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as AirDatepickerPosition,
      selectedDates: [firstDate],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: ({ formattedDate, date }) => {
        if (formattedDate) {
          const newStartDateCnt = formattedDate as string;
          const newStartDateFull = `${newStartDateCnt}:00`;

          const selectedDate = date as Date;

          setDateTime1((prev) => ({ ...prev, start_date: newStartDateFull }));
          setDateTime2((prev) => {
            const currentEndDate = new Date(`${prev.end_date}:00`);
            const interval = calculateInterval(selectedDate, currentEndDate);
            return {
              ...prev,
              start_date: newStartDateCnt,
              interval,
            };
          });

          setActiveButton('');
        }
      },
    };

    const optionsDateSecond: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: 'bottom center' as AirDatepickerPosition,
      selectedDates: [secondDate],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: ({ formattedDate, date }) => {
        if (formattedDate) {
          const newEndDateCnt = formattedDate as string;
          const newEndDateFull = `${newEndDateCnt}:00`;

          const selectedDate = (Array.isArray(date) ? date[0] : date) as Date;

          setDateTime1((prev) => ({ ...prev, end_date: newEndDateFull }));
          setDateTime2((prev) => {
            const currentStartDate = new Date(`${prev.start_date}:00`);
            const interval = calculateInterval(currentStartDate, selectedDate);
            return {
              ...prev,
              end_date: newEndDateCnt,
              interval,
            };
          });

          setActiveButton('');
        }
      },
    };

    // First date picker
    const datepicker1 = new AirDatepicker('[name="first-date"]', optionsDate);
    // Second date picker
    const datepicker2 = new AirDatepicker('[name="second-date"]', optionsDateSecond);

    return () => {
      datepicker1.destroy();
      datepicker2.destroy();
    };
  }, [calculateInterval, dateTime2.end_date, dateTime2.start_date, i18n.language]);

  useEffect(() => {
    if (filteredEventTypeOptions.length > 1) {
      handleSearch(
        searchInput,
        type,
        dateTime1.start_date,
        dateTime1.end_date,
        dateTime2.start_date,
        dateTime2.end_date,
        dateTime2.interval
      );
      const destroyCalendar = calendarMake();
      return destroyCalendar;
    }
    return undefined;
  }, [
    filteredEventTypeOptions,
    handleSearch,
    searchInput,
    type,
    dateTime1.start_date,
    dateTime1.end_date,
    dateTime2.start_date,
    dateTime2.end_date,
    dateTime2.interval,
    calendarMake,
  ]);

  const setOneHour = (): void => {
    const now = new Date();
    const firstDate = new Date(now);
    firstDate.setHours(now.getHours() - 1);
    const secondDate = new Date(now);
    setTimeAndQuery(firstDate, secondDate, 5); // 1 hour / 12 intervals = 5 minutes
  };

  const setOneDay = (): void => {
    const now = new Date();
    const firstDate = new Date(now);
    firstDate.setDate(now.getDate() - 1); // 1일을 빼기
    const secondDate = new Date(now);
    setTimeAndQuery(firstDate, secondDate, 60); // 1 day / 12 intervals = 60 minutes
  };

  const setOneMonth = (): void => {
    const now = new Date();
    const firstDate = new Date(now);
    firstDate.setMonth(now.getMonth() - 1); // 1개월을 빼기
    const secondDate = new Date(now);
    setTimeAndQuery(firstDate, secondDate, 3600); // 1 month (approx 30 days) / 12 intervals = 2.5 days = 3600 minutes
  };

  const setCustomTime = (): void => {
    const startDate = new Date(`${dateTime2.start_date}:00`);
    const endDate = new Date(`${dateTime2.end_date}:00`);
    const interval = calculateInterval(startDate, endDate);
    setTimeAndQuery(startDate, endDate, interval);
  };

  const processChartData = useCallback(
    (
      trafficEventCntData: UseSuddenEventReturn['trafficEventCnt'],
      cntStartDate: string,
      cntEndDate: string
    ) => {
      // Calculate 12 intervals
      const startDate = new Date(`${cntStartDate}:00`);
      const endDate = new Date(`${cntEndDate}:00`);
      const timeDifference = endDate.getTime() - startDate.getTime(); // 밀리초 단위 차이
      const timeInterval = timeDifference / 12; // 12개의 구간으로 나누기

      // 12개 구간을 담을 빈 배열을 선언
      const newCategories: string[] = new Array(12);
      const newFullCategories: string[] = new Array(12); // 새로운 fullCategories 배열 (년도까지 추가)

      // Calculate 12 intervals
      for (let i = 0; i < 12; i++) {
        // Calculate end time for the i-th interval
        const intervalEnd = new Date(startDate.getTime() + timeInterval * (i + 1));
        // Format category label (MM-DD hh:mm)
        const categoryLabel = `${String(intervalEnd.getMonth() + 1).padStart(2, '0')}-${String(intervalEnd.getDate()).padStart(2, '0')} ${String(intervalEnd.getHours()).padStart(2, '0')}:${String(intervalEnd.getMinutes()).padStart(2, '0')}`;
        // Format full category label (YYYY-MM-DD hh:mm)
        const fullCategoryLabel = `${intervalEnd.getFullYear()}-${String(intervalEnd.getMonth() + 1).padStart(2, '0')}-${String(intervalEnd.getDate()).padStart(2, '0')} ${String(intervalEnd.getHours()).padStart(2, '0')}:${String(intervalEnd.getMinutes()).padStart(2, '0')}`;

        newCategories[i] = categoryLabel;
        newFullCategories[i] = fullCategoryLabel;
      }

      // categories 업데이트
      setCategories(newCategories);
      // setFullCategories(newFullCategories);

      // --- Chart Data Processing ---
      let finalUpdatedValues: ChartSeries[] = [];
      let findMaxValue = 0;

      if (Array.isArray(trafficEventCntData?.data)) {
        // 상태 복사 및 0으로 초기화
        const updatedValues: ChartSeries[] = filteredEventTypeOptions.map((option) => ({
          name: option.label,
          data: new Array<number>(12).fill(0),
        }));

        // Map full categories to full datetime strings for comparison
        const fullCategoryTimes = newFullCategories.map((category) =>
          formatFullDateTime(new Date(`${category}:00`))
        );

        // trafficEventCnt.data 배열을 순차적으로 처리
        trafficEventCntData.data.forEach((entry) => {
          // API returns YYYY-MM-DD HH:mm, convert to full datetime for precise comparison
          const startTime = formatFullDateTime(new Date(`${entry.aggregate_start_time}:00`));
          const endTime = formatFullDateTime(new Date(`${entry.aggregate_end_time}:00`));

          // fullCategories 배열의 각 카테고리에 대해 처리
          fullCategoryTimes.forEach((categoryTime, index) => {
            // Check if category time falls within the aggregate interval (inclusive of start, exclusive of end is typical)
            if (categoryTime >= startTime && categoryTime <= endTime) {
              const typeIndex = updatedValues.findIndex((item) => item.name === entry.type);
              if (typeIndex !== -1) {
                // 해당하는 구간의 값을 누적
                if (updatedValues[typeIndex]) {
                  updatedValues[typeIndex].data[index] =
                    (updatedValues[typeIndex].data[index] ?? 0) + entry.cnt;
                }
              }
              // 전체 항목에 값 누적 (assuming '전체' is always the first)
              if (updatedValues[0]) {
                updatedValues[0].data[index] = (updatedValues[0].data[index] ?? 0) + entry.cnt;
              }
            }
          });
        });

        finalUpdatedValues = updatedValues.filter(
          (series) => series.data.some((count) => count > 0) || series.name === '전체' // Always keep '전체' even if it's 0 after filtering
        );

        const totalSeries = updatedValues.find((series) => series.name === '전체');
        if (totalSeries) {
          findMaxValue = Math.max(...totalSeries.data);
        } else {
          findMaxValue = 0;
        }
      } else {
        finalUpdatedValues = filteredEventTypeOptions
          .map((opt) => ({
            name: opt.label,
            data: new Array<number>(12).fill(0),
          }))
          .filter((series) => series.name === '전체'); // Only keep '전체'
        findMaxValue = 0;
      }

      // 상태 업데이트
      setAverageWaitTimeValues(
        finalUpdatedValues.map((item) => ({ ...item, data: [...item.data] }))
      );

      setYaxisMax(findMaxValue > 10 ? findMaxValue + Math.ceil(findMaxValue * 0.1) : 100);
    },
    [filteredEventTypeOptions]
  );

  useEffect(() => {
    processChartData(trafficEventCnt, dateTime2.start_date, dateTime2.end_date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficEventCnt, dateTime2.start_date, dateTime2.end_date, processChartData]); // trafficEventCnt 값이 변경될 때마다 실행

  // Tabulator language settings
  const languageTabulator = useCallback(() => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  }, [t]);

  const optionsTabulator: ReactTabulatorOptions = useMemo(
    () => ({
      debugInvalidOptions: true,
      pagination: true, //enable pagination
      paginationMode: 'remote',
      paginationSize: rowsPerPage, // Use the state variable
      paginationInitialPage: 1,
      rowHeight: 41,
      movableRows: false,
      index: 'id',
      locale: 'ko',
      langs: {
        ko: languageTabulator(),
      },
      resizableRows: false,
      // The footerElement type in Tabulator is HTMLElement | string, so this is fine.
      footerElement: `<div style="padding: 0 20px 0 0; text-align: right;">총 <a id="list_count">0</a> 건</div>`,
      ajaxURL: `${URLS.BACK_DSH}/traffic-event-list/by-time?${queryParamsTime}`,
      ajaxConfig: {
        method: 'GET',
        credentials: 'include',
      },
      // Use SuddenEventListResponse for the response type
      ajaxResponse: (_url, _params, response: SuddenEventListResponse) => {
        return {
          data: response.data.items,
          last: response.data.total_pages,
        };
      },
      dataReceiveParams: {
        last_page: 'last',
      },
      ajaxURLGenerator: (url, _config, params) => {
        let myUrl = url;
        const page = params.page;
        const size = params.size;
        if (page) {
          myUrl += `&page=${page}&size=${size || rowsPerPage}`;
        }
        if (params.sort.length > 0) {
          const field = params.sort[0].field;
          const dir = params.sort[0].dir;
          myUrl += `&sort=${field}&order=${dir}`;
        }
        return myUrl;
      },
      dataLoader: false, // Prevents immediate data load on component mount, wait for explicit setData
      sortMode: 'remote',
      initialSort: [{ column: 'timestamp', dir: 'desc' as const }],
    }),
    [queryParamsTime, languageTabulator]
  );

  useEffect(() => {
    if (tbRef.current && queryParamsTime) {
      const fullApiUrl = `${URLS.BACK_DSH}/traffic-event-list/by-time?${queryParamsTime}`;
      tbRef.current.setData(fullApiUrl, {}, { force: true });
    }
  }, [queryParamsTime]);

  const { apexChart: apexColors, palette } = useColorStore();
  // Theme is read here so the tooltip follows the current light/dark mode. The
  // tooltip marker size/styling is handled globally in src/styles/index.css.
  const isDark = useThemeStore((state) => state.isDark);

  // External function for setting chart options with dynamic props
  const chartOptionsWithProps = (
    customCategories: string[],
    customYAxisMax: number
  ): ApexCharts.ApexOptions => {
    return {
      ...optionsChartBase,
      chart: {
        type: 'line',
        zoom: { enabled: false },
        fontFamily: 'Pretendard GOV',
        toolbar: { show: false },
        foreColor: apexColors.foreColor,
      },
      // Slot 0 = "전체" (aggregate total, neutral), slots 1..8 = 8 event-type series.
      colors: [apexColors.seriesTotal, ...palette.category.slice(0, 8)],
      grid: {
        borderColor: apexColors.gridBorderColor,
      },
      xaxis: {
        categories: customCategories,
      },
      yaxis: {
        min: 0,
        max: customYAxisMax,
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
      },
    };
  };

  // Effect to update the total count display
  useEffect(() => {
    if (data) {
      // TODO: Replace direct DOM access with React ref or state
      const footerElement = document.getElementById('list_count');
      if (footerElement) {
        // total_cnt comes from trafficEventTime response
        footerElement.textContent = data.total_cnt?.toString() || '0';
      }
    }
  }, [data]);

  // --- handleReset can now be defined ---
  const handleReset = useCallback(() => {
    const today = new Date();
    const firstDate = new Date(today);
    firstDate.setHours(0, 0, 0, 0);
    const secondDate = new Date(today);
    const interval = calculateInterval(firstDate, secondDate);
    const resetSearchInput = '';
    const resetType = 'All';

    // 1. Reset state
    setSearchInput(resetSearchInput);
    setSelectedOption('');
    setType(resetType);
    setDateTime1({
      start_date: formatFullDateTime(firstDate),
      end_date: formatFullDateTime(secondDate),
    });
    setDateTime2({
      start_date: formatDateTime(firstDate),
      end_date: formatDateTime(secondDate),
      interval: interval,
    });
    setActiveButton(''); // Clear active button for time range

    // 2. Trigger search with reset values
    handleSearch(
      resetSearchInput,
      resetType,
      formatFullDateTime(firstDate),
      formatFullDateTime(secondDate),
      formatDateTime(firstDate),
      formatDateTime(secondDate),
      interval
    );
  }, [handleSearch, calculateInterval]);

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>통계 &gt; 교통 이벤트</h3>
      </div>
      <ContainerCard>
        <Filtering
          searchRef={searchRef}
          placeholder="사이트 / 접근로"
          disableFiltering={true}
          customWidthSelect="flex grow shrink-0"
          customWidthInput="flex flex-1"
          labelSelect="이벤트 유형 "
          // Use an anonymous function to pass the input value to handleSearch on search button click
          onSearch={(val: string) =>
            handleSearch(
              val,
              type,
              dateTime1.start_date,
              dateTime1.end_date,
              dateTime2.start_date,
              dateTime2.end_date,
              dateTime2.interval
            )
          }
          value={searchInput}
          onReset={handleReset}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
        >
          <div className="flex flex-col lg:flex-row gap-4 w-full items-start lg:items-center">
            <Select
              options={options}
              customWidthSelect="w-full lg:w-[200px] flex-shrink-0"
              className="cursor-pointer"
              name="event-type-filter"
              value={selectedOption}
              onChange={handleFilterChange}
            />
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center lg:ml-auto">
              <div className="flex w-fit gap-2 flex-shrink-0 items-center">
                <span className="text-sm text-gray-500 whitespace-nowrap">기간</span>
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${activeButton === '1시간' ? 'active' : ''}`}
                  label="1시간"
                  onClick={() => handleButtonClick('1시간')}
                />
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${activeButton === '1일' ? 'active' : ''}`}
                  label="1일"
                  onClick={() => handleButtonClick('1일')}
                />
                <Button
                  customButton={`button filtering cursor-pointer hover:opacity-80 ${activeButton === '1개월' ? 'active' : ''}`}
                  label="1개월"
                  onClick={() => handleButtonClick('1개월')}
                />
              </div>
              <div className="flex flex-row gap-2 items-center flex-shrink-0">
                <GeneralInput
                  isDob={true}
                  type="text"
                  value={dateTime2?.start_date}
                  name={'first-date'}
                  customInput="w-[170px]"
                  readonly
                />
                <span className="text-gray-500 flex-shrink-0">~</span>
                <GeneralInput
                  isDob={true}
                  type="text"
                  value={dateTime2?.end_date}
                  name={'second-date'}
                  customInput="w-[170px]"
                  readonly
                />
              </div>
            </div>
          </div>
        </Filtering>
      </ContainerCard>
      <ContainerCard>
        <ReactTabulator
          paginationCounter={true}
          columns={suddenEventTabulator}
          layout={'fitColumns'}
          className="tabulator-custom w-full "
          options={optionsTabulator}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          events={{
            tableBuilt: () => {},
            // Type data loaded as SuddenEvent array for clarity
            dataLoaded: () => {
            },
            pageLoaded: () => {
              // setPage(pageNumber); // Set page if needed to control the API query page parameter
              if (tbRef.current) {
              }
            },
            cellClick: (_e: UIEvent, cell: CellComponent<SuddenEvent>) => {
              const field = cell.getColumn().getField();
              if (field === 'image') {
                const rowData = cell.getRow().getData();
                const imgId = rowData._id;
                const url = `/img-modal?id=${imgId}`;
                window.open(url, '_blank', 'width=686,height=552');
              }
            },
          }}
        />
        <div
          className="mt-[40px] flex flex-col border-[3px] rounded-[3px] p-[10px]"
          style={{ borderColor: 'var(--chart-border)' }}
        >
          <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
            돌발이벤트 발생횟수
          </span>
          <Chart
            options={chartOptionsWithProps(categories, yaxisMax)}
            series={averageWaitTimeValues}
            height={350}
            type={'line'}
            className={'flex w-full !min-h-[0]'}
          />
        </div>
      </ContainerCard>
      {/* <VideoModal/> */}
    </section>
  );
};

export default SuddenEventManagementPage;
